// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Where a hand is, how fast it is going, and where it is about to be (M5-02, M5-06, XR-020).
//
// Hand tracking reports a position and nothing else. Every interaction in 06 §2 needs a
// *velocity*: a flipper button presses on inward travel, a nudge scales with hand speed and
// requires the hand to be moving into the cabinet, and a slam is a speed threshold. All three
// have to come from differencing successive poses, which is the noisiest operation available.
//
// **Why the filter is exponential and not a windowed average.** A window of N frames adds N/2
// frames of lag to a signal whose whole problem is lag (06 §8 budgets ~40 ms for hand tracking
// before any of this). A one-pole filter with a time constant costs one multiply and one add,
// has a lag you can state in milliseconds, and — the part that matters — has a *frame-rate
// independent* coefficient, so the same smoothing happens at 72 Hz in a headset and at 90 Hz on
// a PC-tethered runtime. A fixed `0.8` per frame does not: it is twice as much smoothing at
// double the frame rate, which is how a control that felt right on one device feels sluggish on
// another for no reason anyone can find.
//
// **The exponential needs no `exp`.** `1 − e^(−dt/τ)` is approximated as `dt / (dt + τ)`, which
// is the exact solution of the same one-pole filter discretised bilinearly, agrees with the
// exponential to within 2 % over the frame times a headset ever produces, and is monotone and
// bounded in [0, 1) for every positive `dt`. This is presentation code and not the core, so an
// `exp` would be *allowed* here — it is avoided because a rational function has no accuracy
// cliff and one fewer thing to explain, not because of the determinism rule.

/** A position and its filtered velocity, in metres and metres per second. */
export interface TrackedPose {
  x: number
  y: number
  z: number
  vx: number
  vy: number
  vz: number
  /** True once at least two frames have been seen, so the velocity means something. */
  moving: boolean
}

/** A fresh, unmoving pose at the origin. */
export function emptyPose(): TrackedPose {
  return { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, moving: false }
}

/**
 * Velocity filter time constant, seconds.
 *
 * 40 ms. Chosen against the thing being measured rather than by taste: hand-tracking jitter on
 * Quest sits around a millimetre frame to frame, which differenced at 72 Hz is 0.07 m/s of pure
 * noise, and a 40 ms constant cuts that to well under the 0.15 m/s a nudge needs to be sure of.
 * It costs about 40 ms of lag on a *step* change in velocity, which is one frame more than the
 * velocity lead below gives back.
 */
export const VELOCITY_TAU = 0.040

/**
 * Motion-to-photon latency assumed for hand tracking, seconds.
 *
 * 06 §8 budgets ~40 ms sensor-to-runtime for hands. This is the figure the velocity lead
 * extrapolates by, and it is deliberately **not** the whole budget: leading by more than the
 * true latency makes a fingertip arrive before the finger does, which reads as a flipper that
 * fires when you think about it — uncanny rather than responsive, and it breaks tap passes,
 * because a control that anticipates cannot be timed.
 *
 * Overridable per device: XR-032 measures the real figure on hardware, and the measured number
 * should replace this one rather than sit beside it.
 */
export const HAND_LATENCY_SECONDS = 0.040

/** Controllers report at the display rate with almost no added latency (06 §8: ~4 ms). */
export const CONTROLLER_LATENCY_SECONDS = 0.004

/**
 * Update a pose in place from a new sample.
 *
 * In place because there is one of these per hand per frame at 72 Hz and allocating two objects
 * a frame for the whole session is exactly the kind of steady garbage that shows up as a
 * stutter every few seconds on a mobile GPU.
 *
 * A `frameSeconds` of zero or less leaves the velocity alone rather than dividing by it — two
 * poses at the same timestamp carry no velocity information, and a runtime that reports the same
 * frame twice is not unheard of.
 */
export function trackPose(
  pose: TrackedPose,
  x: number,
  y: number,
  z: number,
  frameSeconds: number,
  tau = VELOCITY_TAU,
): void {
  if (!pose.moving) {
    pose.x = x
    pose.y = y
    pose.z = z
    pose.vx = 0
    pose.vy = 0
    pose.vz = 0
    pose.moving = true
    return
  }
  if (frameSeconds > 0) {
    const inv = 1 / frameSeconds
    const k = frameSeconds / (frameSeconds + tau)
    pose.vx += ((x - pose.x) * inv - pose.vx) * k
    pose.vy += ((y - pose.y) * inv - pose.vy) * k
    pose.vz += ((z - pose.z) * inv - pose.vz) * k
  }
  pose.x = x
  pose.y = y
  pose.z = z
}

/**
 * Forget everything about a pose, so the next sample starts it over.
 *
 * Called when a hand stops being tracked. Without it, a hand that disappears at the left of the
 * play space and reappears at the right differences the two into a velocity of several metres a
 * second — which is a slam tilt (06 §2.3) caused by the player scratching their nose.
 */
export function resetPose(pose: TrackedPose): void {
  pose.moving = false
  pose.vx = 0
  pose.vy = 0
  pose.vz = 0
}

/** Where a pose will be after `seconds`, on the axis given. */
export function ledX(pose: TrackedPose, seconds: number): number {
  return pose.x + pose.vx * seconds
}
export function ledY(pose: TrackedPose, seconds: number): number {
  return pose.y + pose.vy * seconds
}
export function ledZ(pose: TrackedPose, seconds: number): number {
  return pose.z + pose.vz * seconds
}

/** Speed, m/s. */
export function poseSpeed(pose: TrackedPose): number {
  return Math.sqrt(pose.vx * pose.vx + pose.vy * pose.vy + pose.vz * pose.vz)
}
